import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { trainingApi } from "../../services/trainingApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

const STATUS_FILTERS = ["", "ASSIGNED", "IN_PROGRESS", "QUIZ_REQUIRED", "PRACTICAL_REVIEW_REQUIRED", "RETRAINING_REQUIRED", "MANAGER_APPROVAL_REQUIRED", "COMPLETED"];

export default function TrainingAssignmentsPage() {
  const { t } = useLocalization();
  const { canViewTraining, canAssignTraining } = usePermissions();
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [form, setForm] = useState({ employee_id: "", course_id: "", due_date: "" });

  const load = () => {
    trainingApi.listAssignments().then((r) => setAssignments(r.data)).catch(() => {});
    trainingApi.listCourses().then((r) => setCourses(r.data)).catch(() => {});
    trainingApi.listEmployeesForAssignment().then((r) => setEmployees(r.data)).catch(() => {});
  };

  useEffect(() => { if (canViewTraining) load(); }, [canViewTraining]);

  if (!canViewTraining) return <p className="text-sm text-gray-500" data-testid="training-assignments-access-denied">{t("common.accessDenied")}</p>;

  const employeeName = (id) => employees.find((e) => e.id === id)?.legal_name || id;
  const courseName = (id) => courses.find((c) => c.id === id)?.title_en || id;

  const assign = async () => {
    if (!form.employee_id || !form.course_id) { toast.error(t("mgmtTrainingAssignments.chooseEmployeeAndCourse")); return; }
    try {
      await trainingApi.assignCourse(form);
      toast.success(t("mgmtTrainingAssignments.courseAssigned"));
      setForm({ employee_id: "", course_id: "", due_date: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const approve = async (id) => {
    try { await trainingApi.approveAssignment(id); toast.success(t("mgmtTrainingAssignments.assignmentApproved")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = statusFilter ? assignments.filter((a) => a.status === statusFilter) : assignments;

  return (
    <div className="space-y-5" data-testid="training-assignments-page">
      <PageHelpBanner pageKey="training-assignments" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtTrainingAssignments.help") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtTrainingAssignments.title")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtTrainingAssignments.subtitle")}</p>
      </div>

      {canAssignTraining && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="assign-course-form">
          <select data-testid="assign-employee-select" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
            <option value="">{t("mgmtTrainingAssignments.employeeEllipsis")}</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.legal_name} ({e.employee_id})</option>)}
          </select>
          <select data-testid="assign-course-select" value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
            <option value="">{t("mgmtTraining.courses")}...</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title_en} ({c.status})</option>)}
          </select>
          <input data-testid="assign-due-date-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
          <button data-testid="assign-course-btn" onClick={assign} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">{t("mgmtTrainingAssignments.assign")}</button>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {STATUS_FILTERS.map((s) => (
          <button key={s || "ALL"} data-testid={`assignment-filter-${s || "ALL"}`} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${statusFilter === s ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            {s || t("mgmtMedical.allFilter")}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colEmployee")}</th><th className="px-4 py-3 font-medium">{t("mgmtTraining.courses")}</th><th className="px-4 py-3 font-medium">{t("mgmtActionCenter.colDue")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3 font-medium"></th></tr></thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id} className="border-t border-gray-100" data-testid={`assignment-row-${a.id}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">{employeeName(a.employee_id)}</td>
                <td className="px-4 py-2 text-gray-600">{courseName(a.course_id)}</td>
                <td className="px-4 py-2 text-gray-600">{a.due_date || "-"}</td>
                <td className="px-4 py-2"><StatusBadge status={a.status} /></td>
                <td className="px-4 py-2">
                  {a.status === "MANAGER_APPROVAL_REQUIRED" && canAssignTraining && (
                    <button data-testid={`approve-assignment-btn-${a.id}`} onClick={() => approve(a.id)} className="px-2 py-1 rounded text-xs font-semibold bg-green-600 text-white hover:bg-green-700">{t("common.approve")}</button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500" data-testid="no-assignments-row">{t("mgmtTrainingAssignments.noAssignmentsFound")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
